import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  LossKind, LossOutcome, LossQueue, LossReportView, RecallRun, RetailerStock, StockOverview, StockPiece, VerificationView,
} from '@miraki/contracts';
import { api, ApiError } from '../api/client';
import { invalidateAll, settleWrite, stockDeskWrite } from '../api/write-keys';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Money, Note, Panel, Spinner, Table } from '../ui';
import {
  countPayload, firstOutcome, policyLines, recallable, reportable, returnLabel, stockRefusalLines, type CountDraft,
} from './stock-desk';

/**
 * A-43's stock half — CN-09 recall, CN-10 lost / damaged / stolen, CN-11 stock verification.
 *
 * Each rests on a decision the client has not made — D20 (what triggers a recall), D39/D81 (what a
 * loss costs the retailer), D40 (how often to count) — and the screen says which rule it is acting
 * under at the top, so "nothing is due" under an open D20 is never mistaken for "nothing is old".
 *
 * Three permissions, each gating its own controls; the server enforces them regardless:
 *   stock.verify        the retailer list, the count, and reporting a loss
 *   stock.loss_outcome  the loss queue and its outcomes — CHARGED posts to the ledger
 *   return.recall       recalling pieces, and running the D20 trigger by hand
 *
 * Budget is never shown moving here as money: a reported or decided piece leaves budget in use
 * because of its status (BG-09), and a CHARGE is the only amount a retailer will owe.
 */

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');
type Amount = { currency: string; amount: string };

function Amounts({ list }: { list: readonly Amount[] }) {
  if (list.length === 0) return <span className="mk-dim">—</span>;
  return <>{list.map((a) => <div key={a.currency}><Money amount={a.amount} currency={a.currency} /></div>)}</>;
}

function useFailure() {
  const { t } = useTranslation();
  const [lines, setLines] = useState<string[] | null>(null);
  const show = (err: unknown, jobOf: (id: string) => string | undefined = () => undefined) => {
    const r = stockRefusalLines(err, jobOf);
    setLines(r ? r.map((l) => t(l.key, l.values)) : [err instanceof ApiError ? err.message : t('stock.failed')]);
  };
  const view = lines ? <Alert tone="stop"><ul>{lines.map((l) => <li key={l}>{l}</li>)}</ul></Alert> : null;
  return { show, clear: () => setLines(null), view };
}

export function StockDesk() {
  const { t } = useTranslation();
  const { portal, can } = useSession();
  const qc = useQueryClient();
  const mayCount = can('stock.verify');
  const mayDecide = can('stock.loss_outcome');
  const mayRecall = can('return.recall');
  const base = ['portal', portal?.code ?? null];
  const overview = useQuery({ queryKey: [...base, 'stock'], queryFn: () => api.get<StockOverview>('/stock'), enabled: mayCount });
  const [selected, setSelected] = useState<string | null>(null);
  const [ran, setRan] = useState<string | null>(null);
  const failure = useFailure();

  const run = async () => {
    failure.clear(); setRan(null);
    try {
      const out = await api.post<RecallRun>('/returns/recalls/run');
      setRan(out.raised.length ? t('stock.ranRecalls', { n: out.raised.length }) : t('stock.ranNone'));
      await invalidateAll(qc, [base]);
    } catch (e) { failure.show(e); }
  };

  return (
    <>
      {mayCount ? (
        overview.isLoading ? <Spinner /> : overview.error || !overview.data ? <Alert tone="stop">{t('stock.failed')}</Alert> : (
          <>
            <Panel title={t('stock.policyTitle')} pad>
              <ul>{policyLines(overview.data.policy).map((l) => <li key={l.key}>{t(l.key, l.values)}</li>)}</ul>
              <p className="mk-hint">{t('stock.policy.setOn')}</p>
              {mayRecall && overview.data.policy.recallTrigger.type !== 'NONE'
                ? <Button size="sm" tone="ghost" onClick={() => void run()}>{t('stock.runRecalls')}</Button> : null}
              {ran ? <Alert tone="done">{ran}</Alert> : null}
              {failure.view}
            </Panel>

            <Panel title={t('stock.retailersTitle')}>
              <Table
                rows={[...overview.data.retailers]}
                rowKey={(r) => r.retailerId}
                rowClassName={(r) => (r.retailerId === selected ? 'on' : '')}
                columns={[
                  { header: t('stock.colRetailer'), render: (r) => <><b>{r.retailerName}</b><div className="mk-hint">{t('stock.account', { status: r.accountStatus })}</div></> },
                  { header: t('stock.colHeld'), numeric: true, render: (r) => r.piecesHeld },
                  { header: t('stock.colValue'), numeric: true, render: (r) => <Amounts list={r.heldValue} /> },
                  { header: t('stock.colOldest'), numeric: true, render: (r) => r.oldestDaysHeld ?? '—' },
                  { header: t('stock.colDue'), numeric: true, render: (r) => (r.dueForRecall ? <Chip tone="stop">{r.dueForRecall}</Chip> : '—') },
                  { header: t('stock.colLosses'), numeric: true, render: (r) => (r.openLosses ? <Chip tone="us">{r.openLosses}</Chip> : '—') },
                  { header: t('stock.colCounted'), render: (r) => r.lastCountedOn ?? <span className="mk-dim">{t('stock.never')}</span> },
                  {
                    header: t('stock.colCountDue'),
                    render: (r) => (r.countDue === null ? <span className="mk-dim">{t('stock.noCycle')}</span>
                      : r.countDue ? <Chip tone="us">{t('stock.yes')}</Chip> : t('stock.no')),
                  },
                  { header: '', render: (r) => <Button size="sm" tone="ghost" onClick={() => setSelected(r.retailerId)}>{t('stock.open')}</Button> },
                ]}
                empty={<Empty title={t('stock.noRetailers')} />}
              />
            </Panel>
            {selected ? <RetailerPanel key={selected} id={selected} base={base} /> : null}
          </>
        )
      ) : null}
      {mayDecide ? <LossQueuePanel base={base} /> : null}
    </>
  );
}

// ------------------------------------------------------------------ one retailer

function RetailerPanel({ id, base }: { id: string; base: unknown[] }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const qc = useQueryClient();
  const key = [...base, 'stock', 'retailer', id];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => api.get<RetailerStock>(`/stock/retailers/${id}`) });
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [action, setAction] = useState<'RECALL' | 'REPORT' | null>(null);
  const [fromCount, setFromCount] = useState<{ pieceId: string; jobNumber: string; kind: LossKind; verificationId: string } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const failure = useFailure();

  if (isLoading) return <Spinner />;
  if (error || !data) return <Alert tone="stop">{t('stock.failed')}</Alert>;

  const jobOf = (pid: string) => data.pieces.find((p) => p.pieceId === pid)?.jobNumber;
  const selected = data.pieces.filter((p) => picked.has(p.pieceId));
  const mayRecall = can('return.recall') && selected.length > 0 && selected.every(recallable);
  const mayReport = can('stock.verify') && selected.length === 1 && reportable(selected[0]!);
  const open = data.verifications.find((v) => v.completedAt === null) ?? null;
  const earlier = data.verifications.filter((v) => v.completedAt !== null);
  const refresh = async (msg: string) => {
    setDone(msg); setAction(null); setFromCount(null); setPicked(new Set());
    await invalidateAll(qc, [base]);
  };
  const toggle = (pid: string) => setPicked((s) => { const n = new Set(s); if (n.has(pid)) n.delete(pid); else n.add(pid); return n; });

  return (
    <Panel title={t('stock.heldTitle', { name: data.retailer.name, n: data.pieces.length })} pad>
      <p className="mk-hint">{t('stock.account', { status: data.retailer.accountStatus })}</p>
      {done ? <Alert tone="done">{done}</Alert> : null}
      {failure.view}
      <Table
        rows={[...data.pieces]}
        rowKey={(p) => p.pieceId}
        columns={[
          {
            header: '', width: '1%',
            render: (p: StockPiece) => <input type="checkbox" aria-label={t('stock.pick', { job: p.jobNumber })} checked={picked.has(p.pieceId)} onChange={() => toggle(p.pieceId)} />,
          },
          { header: t('stock.colJob'), render: (p) => <><b>{p.jobNumber}</b><div className="mk-hint">{p.description}</div></> },
          { header: t('stock.colValue'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
          { header: t('stock.colDays'), numeric: true, render: (p) => p.daysHeld ?? '—' },
          {
            header: t('stock.colReturn'),
            render: (p) => {
              const label = returnLabel(p.returnRequest);
              return (
                <>
                  {label ? <Chip tone="run">{t(`stock.returnState.${label}`)}</Chip> : null}
                  {p.dueForRecall ? <Chip tone="stop">{t('stock.due')}</Chip> : null}
                </>
              );
            },
          },
        ]}
      />
      <p>
        {can('return.recall') ? <Button size="sm" disabled={!mayRecall} onClick={() => setAction('RECALL')}>{t('stock.recallSelected', { n: selected.length })}</Button> : null}{' '}
        {can('stock.verify') ? <Button size="sm" tone="ghost" disabled={!mayReport} onClick={() => setAction('REPORT')}>{t('stock.reportOne')}</Button> : null}
      </p>
      {action === 'RECALL' ? (
        <RecallForm retailerId={id} pieces={selected} onDone={() => void refresh(t('stock.recallSent'))} onError={(e) => failure.show(e, jobOf)} />
      ) : null}
      {action === 'REPORT' && selected[0] ? (
        <LossForm retailerId={id} piece={{ pieceId: selected[0].pieceId, jobNumber: selected[0].jobNumber }}
          onDone={() => void refresh(t('stock.reported'))} onError={(e) => failure.show(e, jobOf)} />
      ) : null}

      <h3>{t('stock.countTitle')}</h3>
      {open ? (
        <CountSheet v={open} onDone={() => void refresh(t('stock.countRecorded'))} onError={(e) => failure.show(e)} />
      ) : can('stock.verify') ? (
        <Button size="sm" onClick={() => void (async () => {
          failure.clear();
          try { await api.post(`/stock/retailers/${id}/verifications`); await invalidateAll(qc, [base]); } catch (e) { failure.show(e); }
        })()}>{t('stock.startCount')}</Button>
      ) : null}

      {earlier.length ? (
        <>
          <h3>{t('stock.countsTitle')}</h3>
          {earlier.map((v) => (
            <div key={v.id}>
              <p>
                <b>{t('stock.countedOn', { day: v.countedOn ?? '' })}</b> · {t('stock.discrepancies', { n: v.discrepancies })}
                {v.remarks ? <span className="mk-hint"> — {v.remarks}</span> : null}
              </p>
              {v.discrepancies ? (
                <Table
                  rows={v.lines.filter((l) => l.result === 'MISSING' || l.result === 'DAMAGED')}
                  rowKey={(l) => l.pieceId}
                  columns={[
                    { header: t('stock.colJob'), render: (l) => <><b>{l.jobNumber}</b><div className="mk-hint">{l.description}</div></> },
                    { header: '', render: (l) => <Chip tone="stop">{t(`stock.result.${l.result!}`)}</Chip> },
                    { header: '', render: (l) => (l.note ? <span className="mk-hint">{l.note}</span> : null) },
                    {
                      header: '',
                      render: (l) => (l.lossReportId || l.pieceStatus !== 'HELD' ? <span className="mk-dim">{t('stock.alreadyReported')} ({l.pieceStatus})</span>
                        : can('stock.verify') ? (
                          <Button size="sm" tone="ghost" onClick={() => setFromCount({
                            pieceId: l.pieceId, jobNumber: l.jobNumber, kind: l.result === 'DAMAGED' ? 'DAMAGED' : 'LOST', verificationId: v.id,
                          })}>{t('stock.reportFromCount')}</Button>
                        ) : null),
                    },
                  ]}
                />
              ) : null}
            </div>
          ))}
          {fromCount ? (
            <LossForm retailerId={id} piece={fromCount} kind={fromCount.kind} verificationId={fromCount.verificationId}
              onDone={() => void refresh(t('stock.reported'))} onError={(e) => failure.show(e, () => fromCount.jobNumber)} />
          ) : null}
        </>
      ) : null}

      <h3>{t('stock.lossesTitle')}</h3>
      <LossTable reports={data.losses} />
    </Panel>
  );
}

function RecallForm({ retailerId, pieces, onDone, onError }: {
  retailerId: string; pieces: StockPiece[]; onDone: () => void; onError: (e: unknown) => void;
}) {
  const { t } = useTranslation();
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="mk-fset">
      <Field label={t('stock.recallRemarks')} hint={t('stock.recallRemarksHint')}>
        <textarea rows={2} value={remarks} maxLength={1000} onChange={(e) => setRemarks(e.target.value)} />
      </Field>
      <Button size="sm" disabled={busy || remarks.trim().length < 3} onClick={() => void (async () => {
        setBusy(true);
        try { await api.post('/returns/recalls', { retailerId, pieceIds: pieces.map((p) => p.pieceId), remarks: remarks.trim() }); onDone(); } catch (e) { onError(e); } finally { setBusy(false); }
      })()}>{t('stock.recallSelected', { n: pieces.length })}</Button>
    </div>
  );
}

function LossForm({ retailerId, piece, kind: preset, verificationId, onDone, onError }: {
  retailerId: string; piece: { pieceId: string; jobNumber: string }; kind?: LossKind; verificationId?: string;
  onDone: () => void; onError: (e: unknown) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<LossKind>(preset ?? 'LOST');
  const [account, setAccount] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="mk-fset">
      <p><b>{t('stock.reportFor', { job: piece.jobNumber })}</b></p>
      <Field label={t('stock.kindLabel')}>
        <select value={kind} onChange={(e) => setKind(e.target.value as LossKind)}>
          {(['LOST', 'DAMAGED', 'STOLEN'] as const).map((k) => <option key={k} value={k}>{t(`stock.kind.${k}`)}</option>)}
        </select>
      </Field>
      <Field label={t('stock.accountLabel')} hint={t('stock.accountHint')}>
        <textarea rows={2} value={account} maxLength={1000} onChange={(e) => setAccount(e.target.value)} />
      </Field>
      <Note>{t('stock.reportGoes')}</Note>
      <Button size="sm" disabled={busy || account.trim().length < 10} onClick={() => void (async () => {
        setBusy(true);
        try {
          await api.post(`/stock/retailers/${retailerId}/losses`, { pieceId: piece.pieceId, kind, account: account.trim(), ...(verificationId ? { verificationId } : {}) });
          onDone();
        } catch (e) { onError(e); } finally { setBusy(false); }
      })()}>{t('stock.sendReport')}</Button>
    </div>
  );
}

function CountSheet({ v, onDone, onError }: { v: VerificationView; onDone: () => void; onError: (e: unknown) => void }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<CountDraft>({});
  const [countedOn, setCountedOn] = useState('');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const out = countPayload(v.lines, draft, countedOn, remarks);
  const set = (pid: string, patch: { result?: 'PRESENT' | 'MISSING' | 'DAMAGED'; note?: string }) =>
    setDraft((d) => ({ ...d, [pid]: { ...d[pid], ...patch } }));
  return (
    <>
      <p className="mk-hint">{t('stock.countStarted', { day: day(v.startedAt), n: v.lines.length })}</p>
      <Table
        rows={[...v.lines]}
        rowKey={(l) => l.pieceId}
        columns={[
          { header: t('stock.colJob'), render: (l) => <><b>{l.jobNumber}</b><div className="mk-hint">{l.description}</div></> },
          {
            header: '',
            render: (l) => (
              <div role="radiogroup" aria-label={t('stock.resultFor', { job: l.jobNumber })}>
                {(['PRESENT', 'MISSING', 'DAMAGED'] as const).map((r) => (
                  <label key={r}><input type="radio" name={`c-${l.pieceId}`} checked={draft[l.pieceId]?.result === r} onChange={() => set(l.pieceId, { result: r })} /> {t(`stock.result.${r}`)} </label>
                ))}
              </div>
            ),
          },
          {
            header: '',
            render: (l) => <input className="mk-fld" aria-label={t('stock.noteFor', { job: l.jobNumber })} maxLength={500}
              value={draft[l.pieceId]?.note ?? ''} onChange={(e) => set(l.pieceId, { note: e.target.value })} />,
          },
        ]}
      />
      <div className="mk-fset">
        <Field label={t('stock.countOn')}><input type="date" value={countedOn} onChange={(e) => setCountedOn(e.target.value)} /></Field>
        <Field label={t('stock.countRemarks')} hint={t('stock.countRemarksHint')}>
          <textarea rows={2} value={remarks} maxLength={2000} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
      </div>
      <Note>{t('stock.countNoMove')}</Note>
      {!out.ok && out.missing.length ? <p className="mk-hint">{t('stock.countMissing', { jobs: out.missing.join(', ') })}</p> : null}
      <Button size="sm" disabled={!out.ok || busy} onClick={() => out.ok && void (async () => {
        setBusy(true);
        try { await api.post(`/stock/verifications/${v.id}/complete`, out.payload); onDone(); } catch (e) { onError(e); } finally { setBusy(false); }
      })()}>{t('stock.completeCount')}</Button>
    </>
  );
}

// ------------------------------------------------------------------ CN-10 — the loss queue

function LossTable({ reports, decide }: { reports: readonly LossReportView[]; decide?: (r: LossReportView) => ReactNode }) {
  const { t } = useTranslation();
  return (
    <Table
      rows={[...reports]}
      rowKey={(r) => r.id}
      columns={[
        { header: t('stock.colJob'), render: (r) => <><b>{r.jobNumber}</b><div className="mk-hint">{r.description}</div></> },
        ...(decide ? [{ header: t('stock.colRetailer'), render: (r: LossReportView) => r.retailerName }] : []),
        { header: t('stock.colValue'), numeric: true, render: (r) => <Money amount={r.wholesaleValue} currency={r.currency} /> },
        {
          header: t('stock.colKind'),
          render: (r) => <>{t(`stock.kind.${r.kind}`)} · {t(`stock.by.${r.reportedByType}`)} · {day(r.reportedAt)}<div className="mk-hint">{r.account}</div></>,
        },
        {
          header: t('stock.colOutcome'),
          render: (r) => (
            <>
              <Chip tone={r.outcome === null ? 'us' : r.outcome === 'CHARGED' ? 'stop' : r.outcome === 'INSURANCE_CLAIM' ? 'run' : 'done'}>
                {t(`stock.outcome.${r.outcome ?? 'OPEN'}`)}
              </Chip>
              {r.outcomeReason ? <div className="mk-hint">{r.outcomeReason}</div> : null}
              {decide && r.next.length ? decide(r) : null}
            </>
          ),
        },
      ]}
      empty={<Empty title={t('stock.noLosses')} />}
    />
  );
}

function LossQueuePanel({ base }: { base: unknown[] }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({ queryKey: [...base, 'stock', 'losses'], queryFn: () => api.get<LossQueue>('/stock/losses') });
  if (isLoading) return <Spinner />;
  if (error || !data) return <Alert tone="stop">{t('stock.failed')}</Alert>;
  return (
    <Panel title={t('stock.lossesTitle')}>
      <LossTable reports={data.reports} decide={(r) => <OutcomeForm key={r.id} r={r} preset={firstOutcome(r.next, data.lossDefaultOutcome)} />} />
    </Panel>
  );
}

function OutcomeForm({ r, preset }: { r: LossReportView; preset: LossOutcome | '' }) {
  const { t } = useTranslation();
  const { portal } = useSession();
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<LossOutcome | ''>(preset);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const failure = useFailure();
  return (
    <div className="mk-fset">
      <Field label={t('stock.outcomeFor', { job: r.jobNumber })}>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value as LossOutcome | '')}>
          <option value="">{t('stock.chooseOutcome')}</option>
          {r.next.map((o) => <option key={o} value={o}>{t(`stock.outcome.${o}`)}</option>)}
        </select>
      </Field>
      <Field label={t('stock.outcomeReason')}><input className="mk-fld" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} /></Field>
      {outcome === 'CHARGED' ? <Alert tone="them">{t('stock.chargeWarning')} <Money amount={r.wholesaleValue} currency={r.currency} /></Alert> : null}
      {failure.view}
      <Button size="sm" disabled={busy || outcome === '' || reason.trim().length < 10} onClick={() => void (async () => {
        setBusy(true); failure.clear();
        try {
          await api.post(`/stock/losses/${r.id}/outcome`, { outcome, reason: reason.trim() });
          /*
           * This is the one await on this screen that holds a button disabled, so it is the one
           * that must not wait for the whole portal. It used to await `['portal', code]`, which
           * covers the admin shell's "Waiting for your approval" banner — mounted and polled on
           * every page — so a slow poll there left "Record outcome" dead over an outcome the
           * ledger had already taken. This desk's own queries all sit under `stock`, and the rest
           * of the portal is still re-read, just not waited for. See `write-keys.ts`.
           */
          await settleWrite(qc, stockDeskWrite(portal?.code ?? null));
        } catch (e) { failure.show(e, () => r.jobNumber); } finally { setBusy(false); }
      })()}>{t('stock.recordOutcome')}</Button>
    </div>
  );
}
